import * as React from "react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Lock, Mail, AlertTriangle } from "lucide-react";
import { authService } from "../services/authService";
import { configService } from "../../../shared/services/configService";

interface LoginScreenProps {
  onLoginSuccess: (userId: string) => void;
}

const AUTH_MS_LOG_PREFIX = "[AUTH_MS]";

function authMsLog(level: "log" | "warn" | "error", payload: Record<string, unknown>): void {
  console[level](AUTH_MS_LOG_PREFIX, {
    ts: new Date().toISOString(),
    ...payload,
  });
}

function MicrosoftLogoMark(): React.JSX.Element {
  return (
    <svg className="w-4 h-4 shrink-0" viewBox="0 0 23 23" aria-hidden>
      <path fill="#f3f3f3" d="M0 0h23v23H0z" />
      <path fill="#f35325" d="M1 1h10v10H1z" />
      <path fill="#81bc06" d="M12 1h10v10H12z" />
      <path fill="#05a6f0" d="M1 12h10v10H1z" />
      <path fill="#ffba08" d="M12 12h10v10H12z" />
    </svg>
  );
}

const LoginScreen: React.FC<LoginScreenProps> = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [microsoftLoading, setMicrosoftLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isAnyLoading = passwordLoading || microsoftLoading;

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      setError("Please enter your email and password");
      return;
    }

    setPasswordLoading(true);
    setError(null);

    try {
      const result = await authService.signIn(email, password);

      if (result.success) {
        const userId = await authService.getUserId();
        if (userId) {
          onLoginSuccess(userId);
        } else {
          setError("Error retrieving user information");
        }
      } else {
        setError(result.error || "Login failed");
      }
    } catch (err) {
      setError("Unexpected error during login");
      console.error("Login error:", err);
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    const startedAt = Date.now();
    authMsLog("log", {
      stage: "login_click",
      status: "start",
      deploymentMode: configService.getDeploymentMode(),
      isB2C: configService.isB2C(),
      origin: window.location.origin,
      path: window.location.pathname,
    });
    setMicrosoftLoading(true);
    setError(null);

    try {
      const result = await authService.signInWithMicrosoft();
      authMsLog(result.success ? "log" : "error", {
        stage: "login_result",
        status: result.success ? "ok" : "error",
        elapsedMs: Date.now() - startedAt,
        success: result.success,
        userId: result.userId,
        errorMessage: result.error,
      });

      if (!result.success) {
        if (result.error) {
          authMsLog("warn", {
            stage: "ui_error_set",
            status: "error",
            elapsedMs: Date.now() - startedAt,
            shownError: result.error,
          });
          setError(result.error);
        }
        return;
      }

      const userId = result.userId || (await authService.getUserId());
      if (userId) {
        authMsLog("log", {
          stage: "login_success",
          status: "ok",
          elapsedMs: Date.now() - startedAt,
          userId,
        });
        onLoginSuccess(userId);
      } else {
        authMsLog("error", {
          stage: "login_success_missing_user",
          status: "error",
          elapsedMs: Date.now() - startedAt,
          errorCategory: "missing_user_id_after_success",
        });
        setError("Could not load your account after signing in with Microsoft.");
      }
    } catch (err) {
      authMsLog("error", {
        stage: "login_exception",
        status: "error",
        elapsedMs: Date.now() - startedAt,
        errorCategory: "unexpected_exception",
        errorMessage: err instanceof Error ? err.message : String(err),
      });
      setError("Unexpected error during Microsoft sign-in.");
      console.error("Microsoft login error:", err);
    } finally {
      authMsLog("log", {
        stage: "login_click",
        status: "end",
        elapsedMs: Date.now() - startedAt,
      });
      setMicrosoftLoading(false);
    }
  };

  const inputClassName =
    "block w-full pl-10 pr-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all disabled:opacity-50";

  return (
    <div className="flex flex-col items-center justify-start min-h-screen bg-white p-6 sm:p-8 box-border animate-fade-in overflow-y-auto">
      <div className="w-full max-w-sm flex flex-col gap-8">
        <div className="text-center">
          <div className="inline-flex items-center justify-center gap-2.5 mb-6">
            <img
              src="assets/workops-favicon-light.svg"
              alt=""
              width={32}
              height={32}
              className="flex-shrink-0 w-8 h-8"
            />
            <span className="text-lg font-semibold text-slate-900 tracking-tight">WorkOps</span>
          </div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight m-0">Welcome back</h1>
          <p className="mt-1.5 text-slate-500 text-sm m-0">Sign in to your account to continue</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-5">
          <div className="space-y-1.5">
            <label htmlFor="addin-email" className="block text-sm font-medium text-slate-700">
              Email
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail size={16} className="text-slate-400" />
              </div>
              <input
                id="addin-email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isAnyLoading}
                className={inputClassName}
                placeholder="you@company.com"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="addin-password" className="block text-sm font-medium text-slate-700">
              Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock size={16} className="text-slate-400" />
              </div>
              <input
                id="addin-password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isAnyLoading}
                className={inputClassName}
                placeholder="••••••••"
              />
            </div>
          </div>

          <div className="flex items-center justify-between gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                id="remember"
                checked={rememberMe}
                onCheckedChange={(checked) => setRememberMe(checked === true)}
                disabled={isAnyLoading}
              />
              <span className="text-sm text-slate-600">Remember me</span>
            </label>

            <a
              href="https://app.useworkops.com/forgot-password"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-slate-600 hover:text-primary transition-colors shrink-0"
            >
              Forgot password?
            </a>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Button type="submit" className="w-full" size="lg" disabled={isAnyLoading}>
            {passwordLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Signing in...
              </>
            ) : (
              "Sign in"
            )}
          </Button>

          {!configService.isB2C() && (
            <>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-100" />
                </div>
                <div className="relative flex justify-center">
                  <span className="px-3 bg-white text-xs text-slate-400 font-medium uppercase tracking-wider">
                    or
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={handleMicrosoftLogin}
                disabled={isAnyLoading}
                className="w-full flex justify-center items-center gap-2.5 py-2.5 px-4 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {microsoftLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <MicrosoftLogoMark />
                    Continue with Microsoft
                  </>
                )}
              </button>
            </>
          )}
        </form>

        {configService.isB2C() && (
          <p className="text-center text-sm text-slate-500">
            Don&apos;t have an account?{" "}
            <a
              href="https://app.useworkops.com/signup"
              className="font-medium text-primary hover:text-primary/80 transition-colors"
              target="_blank"
              rel="noopener noreferrer"
            >
              Sign up
            </a>
          </p>
        )}
      </div>
    </div>
  );
};

export default LoginScreen;
